import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type ReactNode, useState } from 'react';
import { controlHeight, radius } from '@/shared/theme';
import { Icon } from './icon';

export interface FilterMenuOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface FilterMenuProps<T extends string> {
  /** Small uppercase caption above the current value. */
  label: string;
  value: T;
  options: FilterMenuOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Caps the popover height; long option lists scroll. */
  maxHeight?: number;
}

/**
 * Compact dropdown filter of the prototype's marketing screens: caption, current choice and a
 * popover list with a tick on the active option. Used where a full `TextField select` would be
 * too tall for a dense filter row.
 */
export function FilterMenu<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  maxHeight = 360,
}: FilterMenuProps<T>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = options.find((option) => option.value === value);

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        aria-label={label}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          height: controlHeight.md,
          px: '14px',
          borderRadius: radius.control,
          border: `1px solid ${theme.vars.palette.md.border}`,
          background: theme.vars.palette.md.panel,
          '&:hover': { borderColor: theme.vars.palette.md.borderStrong },
          '&.Mui-focusVisible': {
            borderColor: theme.vars.palette.md.primary,
            boxShadow: `0 0 0 4px ${theme.vars.palette.md.primary050}`,
          },
          '&.Mui-disabled': { opacity: 0.55 },
        })}
      >
        <Box
          component="span"
          sx={(theme) => ({
            fontSize: 11,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            fontWeight: 800,
            whiteSpace: 'nowrap',
            color: theme.vars.palette.md.muted,
          })}
        >
          {label}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {current?.label ?? '—'}
        </Box>
        <Box sx={(theme) => ({ display: 'flex', color: theme.vars.palette.md.muted })}>
          <Icon name="chevronDown" size={16} />
        </Box>
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight, minWidth: 220 } } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setAnchor(null);
            }}
            sx={{ display: 'flex', gap: 1.5, fontSize: 13.5 }}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {option.label}
            </Box>
            {option.value === value && (
              <Box sx={(theme) => ({ display: 'flex', color: theme.vars.palette.md.primary })}>
                <Icon name="check" size={15} />
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
